import { without } from "../fields";
import { loadPrice } from "../finds";
import { type AxialCoord, hexDistance } from "../hex";
import { workingCities } from "../land/cities";
import { percentOf } from "../percent";
import { rankBy } from "../pick";
import { addSupply } from "../run/pools";
import { seatOfUnit, soldiersOwnUnit, withSeat } from "../run/seats";
import type { FactionId, Role, RunState, Unit } from "../types/state";
import { replaceUnit } from "../units/unit";
import { payDeed } from "./bounty";
import { comradeBeside, isAtWork } from "./company";
import { purseOf } from "./prospect";

/**
 * Selling a load (M2, C2): the markets a soldier's load can be sold at, their prices and the sale.
 */

/** A city that pays for a load: how far it is from the soldier, what it pays and what it keeps. */
export interface Market {
  readonly city: AxialCoord;
  readonly distance: number;
  readonly price: number;
  readonly cut: number;
  /** The price is the company's (C2): a comrade stands beside the seller. Absent otherwise. */
  readonly company?: true;
}

/**
 * What every working city of the soldier's kingdom would pay for its load (M2), the best
 * price first, ties to the nearer city, then to map order. Empty with empty hands.
 */
export function marketsOf(state: RunState, unit: Unit): Market[] {
  const rules = state.rules.finds;
  const load = unit.carry;
  if (!rules || !load) {
    return [];
  }
  const markets = workingCities(state, unit.owner).map((city) => {
    const price = loadPrice(rules, state.rules.cityReach, load, city.coord);
    return {
      city: city.coord,
      distance: hexDistance(city.coord, unit.coord),
      price,
      cut: percentOf(price, rules.marketCutPercent),
    };
  });
  return rankBy(
    markets,
    (market) => -market.price,
    (market) => market.distance,
  );
}

/**
 * The market at hand: the best-paying working city of its kingdom the soldier stands in or
 * beside, at the price a sale would fetch now: with a comrade beside the seller the company's
 * (C2), `salePercent` of the city's, the cut taken from the larger sum.
 */
export function marketFor(state: RunState, unit: Unit): Market | undefined {
  const market = marketsOf(state, unit).find((candidate) => candidate.distance <= 1);
  const { company, finds } = state.rules;
  if (!market || !company || !finds || !comradeBeside(state, unit)) {
    return market;
  }
  const price = percentOf(market.price, company.salePercent);
  return {
    ...market,
    price,
    cut: percentOf(price, finds.marketCutPercent),
    company: true,
  };
}

/**
 * A soldier seat's unit sells its load at the market at hand (M2): its seat's purse gains the
 * price less the city's cut, the kingdom's pool gains the cut, the hands are empty. Same object
 * back when the run is over, the unit is not the faction's or not a seat's, the word is not the
 * soldier's own (D8: what it carries is its own, as its road is) or no city is at hand. At work
 * (A62) the load's worth is valor too (V1), alone or in company.
 */
export function sellLoad(
  state: RunState,
  faction: FactionId,
  unitId: number,
  issuer: Role,
): RunState {
  const unit = soldiersOwnUnit(state, faction, unitId, issuer);
  const seat = seatOfUnit(state, unitId);
  const market = unit && marketFor(state, unit);
  if (!unit || !seat || !market) {
    return state;
  }
  const emptyHanded = without(unit, "carry");
  const worth = isAtWork(state, unit) ? (unit.carry?.value ?? 0) : 0;
  const sold = withSeat(addSupply(replaceUnit(state, unit, emptyHanded), faction, market.cut), {
    ...seat,
    purse: purseOf(seat) + market.price - market.cut,
  });
  if (!market.company) {
    return payDeed(sold, seat.id, "soloSale", 0, worth);
  }
  // C2: what the company added is the sale's pay less what the city would have paid a lone seller.
  const lonely = marketsOf(state, unit).find((candidate) => candidate.distance <= 1);
  const alone = lonely ? lonely.price - lonely.cut : 0;
  return payDeed(sold, seat.id, "sale", market.price - market.cut - alone, worth);
}
